import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import { DataService } from '../../../../../../services/dashboard/data.service';
import { NtChartRegionInterface } from '../../../../../../shared/interfaces/nt-chart-region.interface';
import { ModalOpenToMarketCurve30DaysComponent } from '../modal-open-to-market-curve-30-days/modal-open-to-market-curve-30-days.component';
import { CommonModule } from '@angular/common';

type Pt = { month: string | Date; value: number };

@Component({
  selector: 'app-open-to-market-curve-30-days',
  standalone: true,
  imports: [ModalOpenToMarketCurve30DaysComponent, CommonModule],
  templateUrl: './open-to-market-curve-30-days.component.html',
  styleUrl: './open-to-market-curve-30-days.component.css',
  host: { style: 'display: block; height: 100%; width: 100%;' }  // to ensure the component fills the card area
})
export class OpenToMarketCurve30DaysComponent implements AfterViewInit, OnDestroy {

  @Input() lineColor1: string = '#003580'; // Actual
  @Input() lineColor2: string = '#00CEBC'; // Forecast
  // title selected in the card-widget header (displayedTitle), shown in the modal
  @Input() widgetTitle: string = '';
  @Output() hasOpenToMarketCurveData = new EventEmitter<boolean>();

  @ViewChild('chartCanvas', { static: true }) chartCanvasRef!: ElementRef<HTMLCanvasElement>;

  private chart?: Chart;

  public isTabletView: boolean = false;
  public hasData: boolean = true;

  public medicineId = 0;
  public countryId = 1;

  // Filled from getOpenToMarketChartInfo (same call as the Open To Market curve).
  // labels -> "Mese 0", "Mese 1"... one per month of the series.
  labels: string[] = [];
  actual: (number | null)[] = [];
  forecast: (number | null)[] = [];
  // Real months (YYYY-MM) behind each "Mese N" label, needed by the modal calls.
  monthLabels: string[] = [];

  // Real start/end date of each "Month N" period, needed by the tooltip and the modal (onClick),
  // since the label itself is now a relative index ("Month 0", "Month 1"...)
  // and no longer contains a real calendar month.
  // Kept per series: Forecast and Actual have their own Step 0, so the same
  // "Month N" can cover different dates in each one.
  private forecastPeriodDates: Map<number, { start: string; end: string }> = new Map();
  private actualPeriodDates: Map<number, { start: string; end: string }> = new Map();

  curvePointData: any = {};
  isMarketCurveOpen: boolean = false;

  gap = 18;
  forecastOffset = 100 + this.gap; // 118
  yAxisMax = this.forecastOffset + 100; // 218

  constructor(private dataService: DataService) {}

  ngAfterViewInit(): void {
    this.medicineId = sessionStorage.getItem('medicineId') ? Number(sessionStorage.getItem('medicineId')) : 0;
    this.countryId = sessionStorage.getItem('countryId') ? Number(sessionStorage.getItem('countryId')) : 1;
    this.isTabletView = window.innerWidth < 1526; // TABLET VIEW

    this.loadChartData();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private formatDateDDMMYYYY(dateStr: string): string {
    // "yyyy-MM-dd" is read as a local calendar date: new Date("yyyy-MM-dd") is UTC
    // and can show the previous day in timezones behind UTC.
    const date = this.parseYmd(dateStr) ?? new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  // ---- Calendar-date helpers (local dates at midnight, no UTC conversion) ----

  // "yyyy-MM-dd" (optionally followed by a time) -> local date, or null if not valid.
  private parseYmd(dateStr: string | null | undefined): Date | null {
    const m = String(dateStr ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return date.getMonth() === Number(m[2]) - 1 ? date : null;
  }

  private toYmd(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  // Step 0 + n calendar months, always anchored on Step 0 and clamped to the end of the
  // month (31/01 -> 28/02 or 29/02 in leap years -> 31/03 -> 30/04...).
  private addMonthsFromStart(start: Date, n: number): Date {
    const lastDay = new Date(start.getFullYear(), start.getMonth() + n + 1, 0).getDate();
    return new Date(start.getFullYear(), start.getMonth() + n, Math.min(start.getDate(), lastDay));
  }

  // Backend rule: Month 1 = [Step 0, Step 0 + 1 month],
  // Month N = [Step 0 + (N-1) months + 1 day, Step 0 + N months]. Month 0 has no range.
  private periodRange(start: Date, n: number): { start: Date; end: Date } {
    const end = this.addMonthsFromStart(start, n);
    if (n === 1) return { start, end };
    const prevEnd = this.addMonthsFromStart(start, n - 1);
    return { start: new Date(prevEnd.getFullYear(), prevEnd.getMonth(), prevEnd.getDate() + 1), end };
  }

  // Period index today falls into: -1 if Step 0 is still in the future, otherwise N >= 1.
  private currentPeriodIndex(start: Date): number {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (today < start) return -1;
    let n = 1;
    while (this.addMonthsFromStart(start, n) < today) n++;
    return n;
  }

  loadChartData(): void {
    this.dataService.getOpenToMarketChartInfo30Days(this.medicineId, this.countryId).subscribe(infoData => {
      // verify data
      console.log('Open To Market Curve 30 Days data:', infoData);
      this.hasData = infoData?.labels?.length > 0 &&
        (infoData.actual.some((val: any) => val !== null) ||
          infoData.target.some((val: any) => val !== null));

      if (!this.hasData) {
        this.hasOpenToMarketCurveData.emit(false);
        // Empty chart: keeps the two sections and the percentages, but no points.
        this.labels = ['', '', '', '', '', ''];
        this.actual = [null, null, null, null, null, null];
        this.forecast = [null, null, null, null, null, null];
        this.monthLabels = [];
        this.destroyChart();
        this.createChart();
        return;
      }

      this.hasOpenToMarketCurveData.emit(true);
      this.buildSeriesFromData(infoData);
      this.destroyChart();
      this.createChart();
    });
  }

   /* same processing as the Open To Market curve: cumulative values, carry-forward, Actual stops at the current month and once it reaches 100%. NOTE: the API now sends relative labels ("Month 0", "Month 1"...) instead of real calendar months (YYYY-MM), plus startPeriodDate/endPeriodDate per point. */
  private buildSeriesFromData(chartData: NtChartRegionInterface): void {
    /* labels are now "Month 0", "Month 1"... so ordering comes from the number inside the string, not from a YYYY-MM date anymore */
    const monthIndexOf = (input: string | Date): number => {
      const raw = String(input).trim();
      const m = raw.match(/(\d+)/);
      if (!m) throw new Error(`Bad month format: ${raw}`);
      return Number(m[1]);
    };

    // Keyed by the numeric period index (like the standard curve normalizes with toYm),
    // so "Month 3" in the points always matches "Month 3" in the labels regardless of formatting.
    const accumulate = (points: Pt[]) => {
      const sums = new Map<number, number>();
      for (const p of points) {
        const k = monthIndexOf(p.month);
        const roundedValue = Math.round(Number(p.value) * 100) / 100;
        sums.set(k, (sums.get(k) ?? 0) + roundedValue);
      }
      const months = [...sums.keys()].sort((a, b) => a - b);
      const acc = new Map<number, number>();
      let running = 0;
      for (const m of months) {
        running += sums.get(m)!;
        running = Math.round(running * 100) / 100;
        acc.set(m, running);
      }
      return acc;
    };

    const firstMonthNum = (acc: Map<number, number>) => {
      if (acc.size === 0) return null;
      return Math.min(...acc.keys());
    };

    const buildSeries = (
      labels: string[],
      acc: Map<number, number>,
      carryForward: boolean,
      maxMonthNum?: number, // for Actual: stop at current month
      stopAt100?: boolean,  // for Actual: stop inserting points once value reaches 100
      zeroBeforeStart?: boolean, // series has a known Step 0: periods before the first value are 0%
    ): (number | null)[] => {
      const startNum = firstMonthNum(acc);
      const out: (number | null)[] = [];
      let last: number | undefined;
      let reachedMax = false;

      for (const label of labels) {
        const num = monthIndexOf(label);

        if (!zeroBeforeStart && (startNum == null || num < startNum)) {
          out.push(null);
          continue;
        }
        if (maxMonthNum != null && num > maxMonthNum) {
          out.push(null);
          continue;
        }
        if (stopAt100 && reachedMax) {
          out.push(null);
          continue;
        }

        const v = acc.get(num);
        if (v != null) {
          out.push(v);
          last = v;
          if (stopAt100 && v >= 100) reachedMax = true;
        } else if (carryForward && last != null) {
          out.push(last);
          if (stopAt100 && last >= 100) reachedMax = true;
        } else if (zeroBeforeStart) {
          // Nothing accumulated yet (before the first value): cumulative is still 0%.
          out.push(0);
          last = 0;
        } else {
          out.push(null);
        }
      }
      return out;
    };

    // Step 0 of each series (optional API fields). Each series only uses its own Step 0.
    const forecastStart = this.parseYmd(chartData.forecastStartDate);
    const actualStart = this.parseYmd(chartData.actualStartDate);

    // Previously "current month" was found by comparing today's date with the
    // YYYY-MM label. Now the label is a relative index:
    // - actualStartDate known -> the period today falls into, counted from Actual's Step 0
    //   (-1 if Step 0 is in the future: no Actual point)
    // - otherwise fallback to the Actual period dates sent with the points:
    //   no dates at all -> no cap, every period in the future -> -1,
    //   otherwise -> last period already started
    const findCurrentMonthIndex = (): number | undefined => {
      if (actualStart) return this.currentPeriodIndex(actualStart);
      const apiDates: { idx: number; start: string }[] = (chartData.actual || [])
        .filter((p: any) => p.startPeriodDate && p.endPeriodDate)
        .map((p: any) => ({ idx: monthIndexOf(p.month), start: p.startPeriodDate }));
      if (apiDates.length === 0) return undefined;
      const today = new Date();
      let cap = -1;
      apiDates.forEach(({ idx, start }) => {
        if (new Date(start) <= today && idx > cap) cap = idx;
      });
      return cap;
    };
    const currentMonthIndex = findCurrentMonthIndex();

    // Every period from Month 0 to the last one needed by either series (labels and points
    // from the API, plus the current Actual period), even if the API skipped some of them.
    const allIndexes = [
      ...(chartData.labels || []),
      ...(chartData.target || []).map(p => p.month),
      ...(chartData.actual || []).map(p => p.month),
    ].map(monthIndexOf);
    const maxIndex = Math.max(0, ...allIndexes, currentMonthIndex ?? 0);
    const labels: string[] = [];
    for (let i = 0; i <= maxIndex; i++) labels.push('Month ' + i);

    // Save the real dates behind each "Month N" of each series: needed by the tooltip
    // and the modal in onClick. With a Step 0 date, every period 1..maxIndex is generated
    // (also the ones the API skipped); without it, only the dates sent with the points are used.
    // Each series only uses its own dates, never the other one's. Month 0 has no range.
    const collectPeriodDates = (
      start: Date | null,
      points: any[],
      target: Map<number, { start: string; end: string }>,
    ) => {
      target.clear();
      if (start) {
        for (let i = 1; i <= maxIndex; i++) {
          const range = this.periodRange(start, i);
          target.set(i, { start: this.toYmd(range.start), end: this.toYmd(range.end) });
        }
        return;
      }
      points.forEach((p: any) => {
        const idx = monthIndexOf(p.month);
        if (idx > 0 && p.startPeriodDate && p.endPeriodDate) {
          target.set(idx, { start: p.startPeriodDate, end: p.endPeriodDate });
        }
      });
    };
    collectPeriodDates(forecastStart, chartData.target || [], this.forecastPeriodDates);
    collectPeriodDates(actualStart, chartData.actual || [], this.actualPeriodDates);

    this.forecast = buildSeries(labels, accumulate(chartData.target || []), /*carryForward*/ true,
      /*maxMonthNum*/ undefined, /*stopAt100*/ false, /*zeroBeforeStart*/ forecastStart != null);
    this.actual = buildSeries(labels, accumulate(chartData.actual || []), /*carryForward*/ true,
      /*maxMonthNum*/ currentMonthIndex, /*stopAt100*/ true, /*zeroBeforeStart*/ actualStart != null);
    // Use the real period index, so the x-axis label always matches the tooltip/modal period.
    this.labels = labels.map(label => 'Mese ' + monthIndexOf(label));
    this.monthLabels = labels;
  }

  onOpenMarketCurveFromWidget(isOpen: boolean): void {
    this.isMarketCurveOpen = isOpen;
  }

  private createChart(): void {
    const canvas = this.chartCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    // Limit every value between 0% and 100%, so nothing goes outside its own section.
    // Empty months (null) stay null, so no point is drawn there.
    const clampPercent = (value: number | null) => value === null ? null : Math.min(100, Math.max(0, value));
    const actualClamped = this.actual.map(clampPercent);

    // Shift the forecast values up into the top section of the scale.
    const forecastShifted: (number | null)[] = [];
    for (let i = 0; i < this.forecast.length; i++) {
      const value = clampPercent(this.forecast[i]);
      forecastShifted.push(value === null ? null : value + this.forecastOffset);
    }

    // A flat line in the middle of the empty gap works as the separator
    // between the two sections. It's just a normal Chart.js dataset, nothing special.
    const separatorValue = 100 + (this.gap / 2);
    const separatorLine: number[] = [];
    for (let i = 0; i < this.labels.length; i++) {
      separatorLine.push(separatorValue);
    }

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.labels,
        datasets: [
          {
            label: 'Forecast',
            data: forecastShifted,
            borderColor: this.lineColor2,
            backgroundColor: this.lineColor2,
            borderWidth: 2,
            clip: false,
            tension: 0.3,
            fill: false,
            pointRadius: this.isTabletView ? 3.9 : 4.5,
            pointHoverRadius: this.isTabletView ? 6 : 7,
            pointHitRadius: this.isTabletView ? 15 : 13,
            pointBackgroundColor: this.lineColor2,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 1.5
          },
          {
            label: 'Actual',
            data: actualClamped,
            borderColor: this.lineColor1,
            backgroundColor: this.lineColor1,
            borderWidth: 2,
            clip: false,
            tension: 0.3,
            fill: false,
            pointRadius: this.isTabletView ? 3.9 : 4.5,
            pointHoverRadius: this.isTabletView ? 6 : 7,
            pointHitRadius: this.isTabletView ? 15 : 13,
            pointBackgroundColor: this.lineColor1,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 1.5
          },
       /*   {
            label: 'Separator',
            data: separatorLine,
            borderColor: 'rgba(15, 23, 42, 0.2)',
            borderWidth: 1,
            borderDash: [4, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 0,
            fill: false,
            tension: 0
          }*/
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        // Same breathing room top and bottom, so the "100%" of Forecast (top) and
        // "Mese 1, Mese 2..." of Actual (bottom) sit at the same distance from the card border.
        layout: {
          padding: { top: 10, bottom: 10 }
        },
        plugins: {
          legend: {
            display: false // no legend, we recover the space for the chart
          },
                   tooltip: {
            filter: (tooltipItem) => tooltipItem.dataset.label !== 'Separator',
            titleFont: {
              size: this.isTabletView ? 7.8 : 9.8,
              family: 'Montserrat, sans-serif',
              weight: 'normal'
            },
            bodyFont: {
              size: this.isTabletView ? 7.8 : 9.8,
              family: 'Montserrat, sans-serif'
            },
            callbacks: {
              title: (tooltipItems) => {
                const dataIndex = tooltipItems[0]?.dataIndex;
                if (dataIndex === undefined) return '';

                const label = this.monthLabels[dataIndex];
                const monthIndex = label ? Number(label.match(/(\d+)/)?.[0]) : NaN;
                // Forecast is dataset 0, Actual is dataset 1: each shows only its own dates.
                const periodDates = tooltipItems[0].datasetIndex === 0 ? this.forecastPeriodDates : this.actualPeriodDates;
                const period = periodDates.get(monthIndex);

                return period
                  ? [this.formatDateDDMMYYYY(period.start), this.formatDateDDMMYYYY(period.end)]
                  : [];
              },
label: (context) => {
  const rawValue = Number(context.raw);
  const realValue = context.datasetIndex === 0 ? rawValue - this.forecastOffset : rawValue;
  return ' ' + realValue.toFixed(2) + '%';
}
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: this.isTabletView ? 6.5 : 8, family: 'Montserrat, sans-serif' } }
          },
          // Second x-axis, decorative only (no dataset uses it). It's the same category
          // labels, just placed at the "0%" line of Forecast instead of the bottom of the chart.
          x2: {
            type: 'category',
            labels: this.labels,
            position: { y: this.forecastOffset },
            grid: { display: false },
            ticks: { font: { size: this.isTabletView ? 6.5 : 8, family: 'Montserrat, sans-serif' } }
          },
          y: {
            min: 0,
            max: this.yAxisMax,
            // Force fixed tick positions: 6 ticks for the bottom section (0,20,...,100)
            // and 6 ticks for the top section (forecastOffset, forecastOffset+20, ..., forecastOffset+100).
            // Because of the GAP, "100" and "forecastOffset" (110) are two separate ticks now,
            // so both "100%" and "0%" can be shown instead of overlapping.
            afterBuildTicks: (axis) => {
              const bottomTicks = [0, 20, 40, 60, 80, 100];
              const topTicks = bottomTicks.map((value) => value + this.forecastOffset);
              const allTicks = bottomTicks.concat(topTicks);
              axis.ticks = allTicks.map((value) => ({ value }));
            },
            ticks: {
              font: { size: this.isTabletView ? 6 : 8, family: 'Montserrat, sans-serif' },
              color: 'rgba(0,0,0,0.4)',
              callback: (tickValue) => {
                const value = Number(tickValue);
                if (value <= 100) {
                  return value + '%'; // bottom section (Actual): 0% -> 100%
                }
                return (value - this.forecastOffset) + '%'; // top section (Forecast): 0% -> 100%
              }
            },
            grid: {
              // The "0%" line of Forecast (top section) gets the same strong gray as the
              // "0%" line of Actual, which is the x-axis baseline (next to Mese 1, Mese 2...).
              color: (context) => context.tick.value === this.forecastOffset ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.06)'
            },
            border: { display: false }
          }
        },
        // Click on a point -> open the modal with the regions of that period ("Mese N").
        // No overlap dropdown needed here: Actual and Forecast live in two separate sections.
        // Every drawn point is clickable (Month 0, carried-forward and 0% points included).
        onClick: (_event, elements) => {
          if (!this.hasData || elements.length === 0) return;

          const clickedElement = elements[0];
          const dataIndex = clickedElement.index;
          // Forecast is dataset 0, Actual is dataset 1.
          const isForecast = clickedElement.datasetIndex === 0;

          const label = this.monthLabels[dataIndex];
          const periodMatch = label?.match(/(\d+)/);
          if (!periodMatch) return;
          const selectedPeriod = Number(periodMatch[1]);

          // Real cumulative percentage from the series data (0-100, same clamp as the
          // drawn point), never the Chart.js value, which is shifted up for Forecast.
          const value = clampPercent((isForecast ? this.forecast : this.actual)[dataIndex] ?? null);
          if (value === null) return;

          // Dates of the clicked series only (Month 0 has none).
          const period = (isForecast ? this.forecastPeriodDates : this.actualPeriodDates).get(selectedPeriod);

          this.curvePointData = {
            type: isForecast ? 'Forecast' : 'Actual',
            selectedPeriod: selectedPeriod,
            value: value,
            startPeriodDate: period?.start ?? null,
            endPeriodDate: period?.end ?? null,
          };
          this.isMarketCurveOpen = true;
        }
      }
    });
  }
}